import { useEffect, useState } from 'react'
import { Redirect } from 'react-router'
import { motion } from 'motion/react'
import { logEvent } from '@/logging'
import PageLoader from '@/authenticated/components/layout/PageLoader'
import useProgramSignupViewModel from './useProgramSignupViewModel'
import ProgramSignupIncentiveSection from './ProgramSignupIncentiveSection'
import ExplainOptiwattModal from './ExplainOptiwattModal'
import {
  ProgramSignupSheet,
  ProgramSignupSheetContent,
  useProgramSignupSheetViewModel,
} from './ProgramSignupSheet'

type ProgramSignupPageProps = {
  programId: number | null
  initialMode?: 'signup' | 'login'
}

export default function ProgramSignupPage({
  programId,
  initialMode,
}: ProgramSignupPageProps) {
  const viewModel = useProgramSignupViewModel(programId)
  const sheetViewModel = useProgramSignupSheetViewModel(
    programId,
    viewModel.totalRewardsDollars,
    initialMode,
  )
  const [isExplainModalOpen, setIsExplainModalOpen] = useState(false)

  useEffect(function hideRecaptchaBadge() {
    function getRecaptchaBadge() {
      return document.querySelector('.grecaptcha-badge') as HTMLElement | null
    }

    function hideBadge() {
      const badge = getRecaptchaBadge()
      if (badge) {
        badge.style.visibility = 'hidden'
        return true
      }
      return false
    }

    function showBadge() {
      const badge = getRecaptchaBadge()
      if (badge) badge.style.visibility = 'visible'
    }

    if (hideBadge()) return showBadge

    const observer = new MutationObserver(() => {
      if (hideBadge()) observer.disconnect()
    })
    observer.observe(document.body, { childList: true, subtree: true })

    return () => {
      observer.disconnect()
      showBadge()
    }
  }, [])

  if (viewModel.authenticated || sheetViewModel.authenticated) {
    logEvent('signUp')
    return <Redirect to="/app" />
  }

  if (viewModel.isLoading) {
    return <PageLoader />
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className="program-signup-page flex min-h-screen flex-col font-[inter] md:flex-row"
    >
      {/* Left: Incentive Section */}
      <div className="w-full md:w-1/2">
        <ProgramSignupIncentiveSection
          programId={programId}
          isGeneric={viewModel.isGeneric}
          programLogoUrl={viewModel.programLogoUrl}
          resolvedSignupText={viewModel.resolvedSignupText}
          rewardsByCategory={viewModel.rewardsByCategory}
          totalRewardsDollars={viewModel.totalRewardsDollars}
          faqUrl={viewModel.faqUrl}
          onSignUpClick={sheetViewModel.openSheet}
          onExplainOptiwattClick={() => setIsExplainModalOpen(true)}
        />
      </div>

      {/* Right: Form Section (desktop only) */}
      <div className="hidden md:w-1/2 items-center justify-center overflow-y-auto bg-white p-16 md:flex box-border">
        <div className="w-full">
          <ProgramSignupSheetContent viewModel={sheetViewModel} />
        </div>
      </div>

      {/* Mobile: Bottom Sheet */}
      <div className="md:hidden">
        <ProgramSignupSheet viewModel={sheetViewModel} />
      </div>

      {/* Desktop: Explain Optiwatt Modal */}
      <ExplainOptiwattModal
        open={isExplainModalOpen}
        onClose={() => setIsExplainModalOpen(false)}
      />
    </motion.div>
  )
}
